(function () {
  const layoutStorageKey = 'thryft:book-collection-layout';
  const bookCollectionSelector = '[data-section-type="collection"][data-book-collection="true"]';
  const productCollectionContextsSelector = '[data-product-collection-contexts]';
  const productCollectionStateKey = 'thryftProductCollection';

  function getSavedLayout() {
    try {
      const layout = localStorage.getItem(layoutStorageKey);
      return layout === 'grid' || layout === 'list' ? layout : null;
    } catch (error) {
      return null;
    }
  }

  function saveLayout(layout) {
    try {
      localStorage.setItem(layoutStorageKey, layout);
    } catch (error) {
      // Storage can be unavailable in private browsing contexts.
    }
  }

  function syncLayoutControls(collection, layout) {
    collection.querySelectorAll('[data-item-layout-toggle-button]').forEach((button) => {
      const isActive = button.dataset.layoutType === layout;
      button.dataset.isActive = isActive;
      button.setAttribute('aria-pressed', String(isActive));
    });
  }

  function initializeBookCollections(root) {
    const context = root || document;
    const collections = context.matches && context.matches(bookCollectionSelector)
      ? [context]
      : context.querySelectorAll(bookCollectionSelector);
    const savedLayout = getSavedLayout();

    collections.forEach((collection) => {
      const layout = savedLayout || collection.dataset.itemLayout;
      collection.dataset.itemLayout = layout;
      syncLayoutControls(collection, layout);
    });
  }

  function normalizePath(url) {
    if (!url) return null;

    try {
      const path = new URL(url, window.location.origin).pathname;
      return path.length > 1 ? path.replace(/\/+$/, '') : path;
    } catch (error) {
      return null;
    }
  }

  function getReferrerPath() {
    if (!document.referrer) return null;

    try {
      const referrer = new URL(document.referrer);
      return referrer.origin === window.location.origin ? normalizePath(referrer.href) : null;
    } catch (error) {
      return null;
    }
  }

  function initializeProductCollectionBreadcrumbs(root) {
    const context = root || document;
    const contextScripts = context.matches && context.matches(productCollectionContextsSelector)
      ? [context]
      : context.querySelectorAll(productCollectionContextsSelector);
    const productPath = normalizePath(window.location.href);
    const referrerPath = getReferrerPath();

    contextScripts.forEach((contextScript) => {
      const breadcrumb = contextScript.closest('.breadcrumbs')?.querySelector('[data-product-collection-breadcrumb]');
      const breadcrumbLink = breadcrumb?.querySelector('[data-product-collection-breadcrumb-link]');
      if (!breadcrumb || !breadcrumbLink || !productPath) return;

      let collections;
      try {
        collections = JSON.parse(contextScript.textContent);
      } catch (error) {
        return;
      }

      const findCollection = (path) => collections.find((collection) => normalizePath(collection.url) === path);
      let activeCollection = findCollection(referrerPath);
      const savedContext = history.state?.[productCollectionStateKey];

      if (activeCollection) {
        const currentState = history.state && typeof history.state === 'object' ? history.state : {};

        try {
          history.replaceState({
            ...currentState,
            [productCollectionStateKey]: {
              productPath,
              collectionUrl: activeCollection.url,
            },
          }, '');
        } catch (error) {
          // History state can be unavailable in embedded browsing contexts.
        }
      } else if (savedContext?.productPath === productPath) {
        activeCollection = findCollection(normalizePath(savedContext.collectionUrl));
      }

      if (!activeCollection) return;

      breadcrumbLink.href = activeCollection.url;
      breadcrumbLink.textContent = activeCollection.title;
      breadcrumb.hidden = false;
    });
  }

  const bookNoteDialog = document.querySelector('[data-book-note-dialog]');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  let activeBookNoteTrigger = null;
  let bookNotePinned = false;
  let bookNoteCloseTimer;
  let bookNoteHideTimer;
  let bookNotePositionFrame;
  let bookNotePointerType = 'mouse';
  let managingBookNoteFocus = false;

  function setBookNoteExpanded(trigger, expanded) {
    if (trigger) trigger.setAttribute('aria-expanded', String(expanded));
  }

  function positionBookNote() {
    bookNotePositionFrame = null;
    if (!bookNoteDialog || !bookNoteDialog.open || !activeBookNoteTrigger || bookNoteDialog.dataset.presentation !== 'popover') return;

    const triggerRect = activeBookNoteTrigger.getBoundingClientRect();
    if (triggerRect.bottom < 0 || triggerRect.top > window.innerHeight) {
      closeBookNote();
      return;
    }

    const margin = 12;
    const gap = 10;
    const dialogWidth = bookNoteDialog.offsetWidth;
    const dialogHeight = bookNoteDialog.offsetHeight;
    const left = Math.min(
      window.innerWidth - dialogWidth - margin,
      Math.max(margin, triggerRect.left + triggerRect.width / 2 - dialogWidth / 2)
    );
    const spaceAbove = triggerRect.top - gap - margin;
    const spaceBelow = window.innerHeight - triggerRect.bottom - gap - margin;
    const placement = spaceAbove >= dialogHeight || spaceAbove >= spaceBelow ? 'top' : 'bottom';
    const preferredTop = placement === 'top' ? triggerRect.top - dialogHeight - gap : triggerRect.bottom + gap;
    const top = Math.min(window.innerHeight - dialogHeight - margin, Math.max(margin, preferredTop));
    const arrowX = Math.min(dialogWidth - 22, Math.max(22, triggerRect.left + triggerRect.width / 2 - left));

    bookNoteDialog.dataset.placement = placement;
    bookNoteDialog.style.setProperty('--book-note-left', `${left}px`);
    bookNoteDialog.style.setProperty('--book-note-top', `${top}px`);
    bookNoteDialog.style.setProperty('--book-note-arrow-x', `${arrowX}px`);
  }

  function requestBookNotePosition() {
    if (bookNotePositionFrame || !bookNoteDialog?.open || bookNoteDialog.dataset.presentation !== 'popover') return;
    bookNotePositionFrame = requestAnimationFrame(positionBookNote);
  }

  function populateBookNote(trigger) {
    bookNoteDialog.querySelector('[data-book-note-title]').textContent = trigger.dataset.bookNoteTitle;
    bookNoteDialog.querySelector('[data-book-note-copy]').textContent = trigger.dataset.bookNoteCopy;
    bookNoteDialog.querySelector('[data-book-note-link]').href = trigger.dataset.bookNoteUrl;
  }

  function openBookNote(trigger, presentation, pinned) {
    if (!bookNoteDialog) return;

    clearTimeout(bookNoteCloseTimer);
    clearTimeout(bookNoteHideTimer);

    if (activeBookNoteTrigger !== trigger) setBookNoteExpanded(activeBookNoteTrigger, false);
    if (bookNoteDialog.open && bookNoteDialog.dataset.presentation !== presentation) bookNoteDialog.close();

    activeBookNoteTrigger = trigger;
    bookNotePinned = pinned;
    populateBookNote(trigger);
    setBookNoteExpanded(trigger, true);
    bookNoteDialog.dataset.presentation = presentation;
    bookNoteDialog.setAttribute('aria-modal', String(presentation === 'sheet'));

    if (!bookNoteDialog.open) {
      if (presentation === 'sheet') {
        bookNoteDialog.showModal();
      } else {
        const previousFocus = document.activeElement;
        managingBookNoteFocus = true;
        bookNoteDialog.show();

        if (bookNoteDialog.contains(document.activeElement)) {
          if (previousFocus && previousFocus !== document.body) {
            previousFocus.focus({ preventScroll: true });
          } else {
            document.activeElement.blur();
          }
        }
        managingBookNoteFocus = false;
      }
    }

    document.body.classList.toggle('book-note-sheet-open', presentation === 'sheet');

    requestAnimationFrame(() => {
      if (presentation === 'popover') positionBookNote();
      bookNoteDialog.classList.add('is-open');

      if (presentation === 'sheet') {
        bookNoteDialog.querySelector('[data-book-note-close]').focus({ preventScroll: true });
      }
    });
  }

  function closeBookNote(returnFocus = false, immediate = false) {
    if (!bookNoteDialog || !bookNoteDialog.open) return;

    clearTimeout(bookNoteCloseTimer);
    clearTimeout(bookNoteHideTimer);
    const trigger = activeBookNoteTrigger;

    setBookNoteExpanded(trigger, false);
    activeBookNoteTrigger = null;
    bookNotePinned = false;
    bookNoteDialog.classList.remove('is-open');
    document.body.classList.remove('book-note-sheet-open');

    const finish = () => {
      managingBookNoteFocus = true;
      if (bookNoteDialog.open && !bookNoteDialog.classList.contains('is-open')) bookNoteDialog.close();
      if (returnFocus && trigger && trigger.isConnected) trigger.focus({ preventScroll: true });
      managingBookNoteFocus = false;
    };

    if (immediate) {
      finish();
    } else {
      bookNoteHideTimer = setTimeout(finish, 160);
    }
  }

  function scheduleBookNoteClose() {
    if (bookNotePinned || !bookNoteDialog || bookNoteDialog.dataset.presentation !== 'popover') return;
    clearTimeout(bookNoteCloseTimer);
    bookNoteCloseTimer = setTimeout(() => closeBookNote(), 120);
  }

  document.addEventListener('pointerover', (event) => {
    const trigger = event.target.closest('[data-book-note-trigger]');
    if (!finePointer.matches || event.pointerType === 'touch' || !trigger || trigger.contains(event.relatedTarget)) return;
    openBookNote(trigger, 'popover', activeBookNoteTrigger === trigger && bookNotePinned);
  });

  document.addEventListener('pointerout', (event) => {
    const trigger = event.target.closest('[data-book-note-trigger]');
    if (!finePointer.matches || !trigger || trigger.contains(event.relatedTarget)) return;
    scheduleBookNoteClose();
  });

  document.addEventListener('focusin', (event) => {
    if (managingBookNoteFocus) return;
    const trigger = event.target.closest('[data-book-note-trigger]');
    if (finePointer.matches && bookNotePointerType !== 'touch' && trigger) {
      openBookNote(trigger, 'popover', activeBookNoteTrigger === trigger && bookNotePinned);
    } else if (bookNoteDialog && bookNoteDialog.contains(event.target)) {
      clearTimeout(bookNoteCloseTimer);
    }
  });

  document.addEventListener('focusout', () => {
    if (!finePointer.matches || bookNotePinned || managingBookNoteFocus) return;
    setTimeout(() => {
      if (!activeBookNoteTrigger?.contains(document.activeElement) && !bookNoteDialog?.contains(document.activeElement)) {
        scheduleBookNoteClose();
      }
    });
  });

  if (bookNoteDialog) {
    bookNoteDialog.addEventListener('pointerenter', () => clearTimeout(bookNoteCloseTimer));
    bookNoteDialog.addEventListener('pointerleave', scheduleBookNoteClose);
    bookNoteDialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeBookNote(true);
    });
    bookNoteDialog.addEventListener('click', (event) => {
      if (event.target === bookNoteDialog) closeBookNote(true);
    });
  }

  document.addEventListener('pointerdown', (event) => {
    if (event.target.closest('[data-book-note-trigger]')) bookNotePointerType = event.pointerType;
  });

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-book-note-trigger]');
    if (trigger) {
      event.preventDefault();

      if (finePointer.matches && bookNotePointerType !== 'touch') {
        if (activeBookNoteTrigger === trigger && bookNotePinned) {
          closeBookNote();
          return;
        }

        openBookNote(trigger, 'popover', true);
        if (event.detail === 0) {
          requestAnimationFrame(() => bookNoteDialog.querySelector('[data-book-note-close]').focus());
        }
      } else {
        openBookNote(trigger, 'sheet', true);
      }
      return;
    }

    if (event.target.closest('[data-book-note-close]')) {
      closeBookNote(true);
      return;
    }

    if (bookNoteDialog?.open && !bookNoteDialog.contains(event.target)) closeBookNote();
  });

  document.addEventListener('scroll', requestBookNotePosition, true);
  window.addEventListener('resize', requestBookNotePosition);
  finePointer.addEventListener('change', () => closeBookNote(false, true));
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Tab' || event.key === 'Enter' || event.key === ' ') bookNotePointerType = 'keyboard';

    if (event.key === 'Escape' && bookNoteDialog?.open) {
      event.preventDefault();
      closeBookNote(true);
    }
  });

  document.addEventListener('click', (event) => {
    const layoutButton = event.target.closest('[data-item-layout-toggle-button]');
    const collection = layoutButton ? layoutButton.closest(bookCollectionSelector) : null;

    if (collection) {
      const layout = layoutButton.dataset.layoutType;
      saveLayout(layout);
      syncLayoutControls(collection, layout);
      return;
    }

    const recommendationButton = event.target.closest('[data-book-recommendation-toggle]');
    if (!recommendationButton) return;

    const recommendation = document.getElementById(recommendationButton.getAttribute('aria-controls'));
    if (!recommendation) return;

    const isExpanded = recommendationButton.getAttribute('aria-expanded') === 'true';
    recommendationButton.setAttribute('aria-expanded', String(!isExpanded));
    recommendation.dataset.expanded = String(!isExpanded);
  });

  document.addEventListener('shopify:section:load', (event) => {
    initializeBookCollections(event.target);
    initializeProductCollectionBreadcrumbs(event.target);
  });

  document.addEventListener('product:variant-change', (event) => {
    const variant = event.detail && event.detail.variant;
    if (!variant) return;

    document
      .querySelectorAll('[data-quick-product] [data-book-selected-condition]')
      .forEach((condition) => {
        condition.textContent = variant.title;
      });
  });

  initializeBookCollections();
  initializeProductCollectionBreadcrumbs();
})();
